// src/components/email/UserMessages.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  gql,
  useQuery,
  useMutation,
  useLazyQuery,
  ApolloError,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client';
import { BookOpen, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Sidebar } from './components/Sidebar';
import { ThreadList } from './components/ThreadList';
import { ThreadDetail } from './components/ThreadDetail';
import { ComposeModal } from './components/ComposeModal';
import { ReplyModal } from './components/ReplyModal';
import { AddressBook } from './components/AddressBook';
//import { TwoFactorLogin } from './components/TwoFactorLogin'; -- it was a bad idea 

const availableContacts = [
  { email: 'test@test.com', name: 'Test User' },
  { email: 'test2@test.com', name: 'Test User 2' },
];

// Types
export interface UserMessage {
  id: string;
  senderId?: number | null;
  senderString?: string | null;
  recipientId: number;
  recipientString?: string | null;
  subject: string;
  body: string;
  rawBody?: string | null;
  isRead: boolean;
  isPinned: boolean;
  isDeleted: boolean;
  createdAt: string;
  toJson?: string[] | undefined;
  ccJson?: string[] | undefined;
  replyJson?: string[] | undefined;
  tags?: string;
  recipients?: string[];
}

interface ListInboxData {
  listUserInboxMessages: UserMessage[];
  listUserInboxMessagesByToken: UserMessage[];
}

interface State {
  loading: boolean;
  error: ApolloError | null;
  data: UserMessage[] | null;
  threads: UserMessage[];
  totalCount: number;
}

export const UserMessages = ({
  userData,
  userEmail,
  userTags,
  hooks,
  logout
} :any) => {
  const { markRead, deleteMessage, respondMessage, sendMessage, getThread, listInbox, updateUser } = hooks;
  const [state, setState] = useState<any>({ loading: false, error: null, data: null, threads: [], totalCount: 0 });


  const [currentSidebarSection, setCurrentSidebarSection] = useState({ folder: 'inbox', filter: 'unread'}) as any;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const [limitState, setLimitState] = useState<number>(200);
  const [pageState, setPageState] = useState<number>(1);

  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyComposeMessage, setReplyComposeMessage] = useState<UserMessage | null>(null);
  const [isAddressBookOpen, setIsAddressBookOpen] = useState(false);
  const [userAddresses, setUserAddresses] = useState<any[]>([]);

  // Initialize addresses from userData
  useEffect(() => {
    setUserAddresses(userData.addresses || []);
  }, [userData.addresses]);
 
  const handlePageChange = (newPage: number) => {
    setPageState(newPage);
    // The useEffect will automatically trigger a refetch when pageState changes
  };

  const handleLimitChange = (newLimit: number) => {
    setLimitState(newLimit);
    setPageState(1); // Reset to first page when limit changes
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setPageState(1);
    setIsSearching(true);
    // Force immediate search by setting debounced term
    setDebouncedSearchTerm(searchTerm);
    // Clear selected message when searching
    setSelectedMessage(null);
  };  const { folder, filter } = currentSidebarSection;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Clear selected message when search term changes
      if (searchTerm.trim() !== debouncedSearchTerm.trim()) {
        setSelectedMessage(null);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    const fetchMessages = async () => {
      setState( (prevState:any) => ({ ...prevState, loading: true }));
      try {
        const offset = (pageState - 1) * limitState;
        // When searching, default to "all" messages to show all search results
        const searchFilter = debouncedSearchTerm.trim() ? `${folder}:all` : `${folder}:${filter}`;
        const result = await listInbox(debouncedSearchTerm, limitState, offset, searchFilter);
        
        // Handle both successful responses and undefined/null data
        const data = result?.data;
        
        if (!data) {
          console.warn('No data returned from listInbox query');
          setState( (prevState:any) => ({ 
            ...prevState, 
            loading: false, 
            threads: [], 
            totalCount: 0,
          }));
          return;
        }

        // Extract messages and totalCount from various possible response structures
        let messages: UserMessage[] = [];
        let totalCount: number = 0;

        // Handle the standard response format
        if (data.listUserInboxMessages) {
          messages = data.listUserInboxMessages.messages || [];
          totalCount = parseInt(data.listUserInboxMessages.totalCount) || 0;
        } else if (data.listUserInboxMessagesByToken) {
          messages = data.listUserInboxMessagesByToken.messages || [];
          totalCount = parseInt(data.listUserInboxMessagesByToken.totalCount) || 0;
        }
        // Handle sent messages response format
        else if (data.listUserSentMessages) {
          messages = data.listUserSentMessages.messages || [];
          totalCount = parseInt(data.listUserSentMessages.totalCount) || 0;
        } else if (data.listUserSentMessagesByToken) {
          messages = data.listUserSentMessagesByToken.messages || [];
          totalCount = parseInt(data.listUserSentMessagesByToken.totalCount) || 0;
        }
        // Handle search response formats
        else if (data.searchUserInboxMessages) {
          messages = data.searchUserInboxMessages.messages || [];
          totalCount = parseInt(data.searchUserInboxMessages.totalCount) || 0;
        } else if (data.searchUserInboxMessagesByToken) {
          messages = data.searchUserInboxMessagesByToken.messages || [];
          totalCount = parseInt(data.searchUserInboxMessagesByToken.totalCount) || 0;
        }

        setState( (prevState:any) => ({ 
          ...prevState, 
          loading: false, 
          threads: messages, 
          totalCount: totalCount,
        }));
      } catch (error) {
        console.error('Error fetching messages:', error);
        setState( (prevState:any) => ({ 
          ...prevState, 
          loading: false, 
          error: error as ApolloError,
          threads: [],
          totalCount: 0
        }));
      } finally {
        setIsSearching(false);
      }
    };

    fetchMessages();
  }, [debouncedSearchTerm, limitState, pageState, folder, filter]);



  const user = useMemo(() => {
    try {
      const tags = JSON.parse(userData.tags||'{}');
 
      const rules = JSON.parse(userData.rules||'[]');
      return {
        addresses: userAddresses || [],
        name: userData.name || '',
        folders: tags.folders || [],
        rules: rules || [],
      };
    } catch (error) {
      console.error(error);
      return {
        addresses: [],
        folders: [],
        rules: [],
      };
    }
    
  }, [userAddresses, userData]);

  //console.log("user", user);

  return (
    <div className="flex flex-col h-screen bg-white text-sm">
      <header className="bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center">
            <h1 className="text-lg font-semibold text-gray-800">Internal Mail</h1>         
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setIsAddressBookOpen(true)}
              className="text-blue-600 inline-flex items-center gap-2"
            >
              <BookOpen size={20} />
              <span>Address Book</span>
            </Button>
            {logout && (
              <Button
                variant="ghost"
                onClick={() => {
                  document.cookie = 'AuthorizationMailSystem=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                  window.location.reload();
                }}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Logout
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
          <span className="text-sm text-gray-600 truncate">
            {userEmail}
          </span>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search messages…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="w-72 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDebouncedSearchTerm('');
                    setPageState(1);
                    setSelectedMessage(null);
                  }}
                  className="absolute right-3 top-[50%] transform -translate-y-1/2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              onClick={handleSearch}
              disabled={isSearching || state.loading}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm"
            >
              {isSearching || state.loading ? 'Searching...' : (searchTerm.trim() ? 'Search' : 'Refresh')}
            </Button>
          </div>
        </div>
      </header>
  
      <div className="flex flex-1 overflow-hidden max-h-[calc(100vh-200px)]">
        <Sidebar
          currentSection={currentSidebarSection}
          onChangeSection={opt => {
            // Reset pagination and clear selected message when switching filters
            setPageState(1);
            setSelectedMessage(null);
            setCurrentSidebarSection({ folder: opt.folder, filter: opt.filter });
          }}
          user={user}
        />
        <div className="flex flex-1 overflow-hidden flex-col">
          {debouncedSearchTerm.trim() && (
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-800 flex-shrink-0">
              Search results for: "<strong>{debouncedSearchTerm}</strong>" ({state.totalCount} found) - showing all messages
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <ThreadList
              threads={state.threads}
              page={pageState}
              limit={limitState}
              totalCount={state.totalCount}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              onSelect={setSelectedMessage}
              loading={state.loading}
            />
          </div>
        </div>

        <div className="flex-1 bg-white overflow-y-auto">
          {selectedMessage && (
            <ThreadDetail
              getThread={getThread}
              onDelete={(threadId: string) => {
                deleteMessage(threadId);
                setState((prevState: State) => ({
                  ...prevState,
                  threads: prevState.threads.filter((t: UserMessage) => t.id !== threadId)
                }));
                setSelectedMessage(null);
              }}
              onReply={(thread: any) => {
                setReplyComposeMessage(thread);
                setIsReplyOpen(true);
              }}
              thread={selectedMessage}
            />
          )}
          {selectedMessage && <ReplyModal
            isOpen={isReplyOpen}
            onRequestClose={() => setIsReplyOpen(false)}
            replyBody={replyBody}
            setReplyBody={setReplyBody}
            onSubmit={async (threadId, body, recipients) => {
              if (!replyComposeMessage) return;
              await respondMessage(threadId, body, recipients);
              setIsReplyOpen(false);
            }}
            
            thread={selectedMessage}
            availableContacts={availableContacts}
            addresses={user.addresses}
            recipients={replyComposeMessage?.recipients}
          />
          }
        </div>
      </div>
  
      {isAddressBookOpen && (
        <AddressBook
          addresses={user.addresses}
          onClose={() => setIsAddressBookOpen(false)}
          
          onUpdate={(addresses) => {
            // strip out __typename before sending
            const clean = addresses.map(({ name, email }) => ({ name, email }));
            console.log("clean addresses →", clean);
            setUserAddresses(clean);
            updateUser({ addresses: clean });
          }}
        />
      )}
      
    </div>
  );
};
